/**
 * MemorialDispute — public form at /legal/memorial-dispute.
 *
 * POSTs to /api/memorial-dispute which persists the claim AND fires a
 * Resend notification email to DISPUTE_NOTIFY_EMAIL. Gracefully degrades
 * to DB-only when Resend is not configured.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLocale } from "@/lib/locale";
import { buildCountryOptions } from "@/lib/countries";
import Header from "@/components/Header";
import BrandMark from "@/components/BrandMark";
import { ChevronLeft, Send, ShieldAlert } from "lucide-react";

export default function MemorialDispute() {
    const { t, locale } = useLocale();
    const options = useMemo(() => buildCountryOptions(locale || "en"), [locale]);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [relationship, setRelationship] = useState("");
    const [country, setCountry] = useState("");
    const [presenceRef, setPresenceRef] = useState("");
    const [description, setDescription] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [success, setSuccess] = useState(null);

    const canSubmit =
        name.trim().length >= 2 &&
        /.+@.+\..+/.test(email) &&
        relationship.trim().length >= 2 &&
        description.trim().length >= 10;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setBusy(true);
        setErr("");
        try {
            const { data } = await api.post("/memorial-dispute", {
                complainant_name: name.trim(),
                complainant_email: email.trim().toLowerCase(),
                relationship_claim: relationship.trim(),
                country: country || null,
                presence_reference: presenceRef.trim() || null,
                description: description.trim(),
            });
            setSuccess(data);
        } catch (_e) {
            setErr(t("dispute.err_submit"));
        }
        setBusy(false);
    };

    if (success) {
        return (
            <div className="grain min-h-screen candle-halo">
                <Header minimal />
                <main className="max-w-2xl mx-auto px-6 md:px-12 pt-20 pb-24" data-testid="dispute-success">
                    <div className="flex items-center gap-4 mb-8">
                        <BrandMark size={48} breathing={false} />
                        <h1 className="h-serif text-4xl md:text-5xl">{t("dispute.success_title")}</h1>
                    </div>
                    <p className="text-ink-textMid text-lg leading-relaxed">
                        {t("dispute.success_body", { email, id: success.dispute_id })}
                    </p>
                    <p className="mt-4 text-xs text-ink-textMid">
                        ID: <code data-testid="dispute-id" className="text-ink-gold">{success.dispute_id}</code>
                    </p>
                    <Link to="/" className="btn-ghost mt-10 inline-block">{t("legal.back")}</Link>
                </main>
            </div>
        );
    }

    return (
        <div className="grain min-h-screen candle-halo">
            <Header minimal />
            <main className="max-w-2xl mx-auto px-6 md:px-12 pt-12 pb-24" data-testid="memorial-dispute">
                <Link to="/legal/memorial-policy" className="inline-flex items-center gap-2 text-ink-textMid hover:text-ink-textHigh text-sm mb-8">
                    <ChevronLeft size={14} /> {t("legal.back")}
                </Link>

                <div className="flex items-center gap-4 mb-10">
                    <ShieldAlert className="text-ink-gold" size={42} />
                    <div>
                        <p className="label-eyebrow text-ink-gold">{t("dispute.eyebrow")}</p>
                        <h1 className="h-serif text-4xl md:text-5xl mt-1">{t("dispute.title")}</h1>
                    </div>
                </div>

                <p className="text-ink-textMid text-lg mb-10 leading-relaxed">{t("dispute.intro")}</p>

                <form onSubmit={submit} className="space-y-6">
                    <div>
                        <label className="label-eyebrow" htmlFor="d-name">{t("dispute.name_label")}</label>
                        <input id="d-name" className="input-line mt-2" value={name} onChange={(e) => setName(e.target.value)} required data-testid="dispute-name" />
                    </div>

                    <div>
                        <label className="label-eyebrow" htmlFor="d-email">{t("dispute.email_label")}</label>
                        <input id="d-email" type="email" className="input-line mt-2" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="dispute-email" />
                    </div>

                    <div>
                        <label className="label-eyebrow" htmlFor="d-rel">{t("dispute.relationship_label")}</label>
                        <input id="d-rel" className="input-line mt-2" placeholder={t("dispute.relationship_ph")} value={relationship} onChange={(e) => setRelationship(e.target.value)} required data-testid="dispute-relationship" />
                    </div>

                    <div>
                        <label className="label-eyebrow" htmlFor="d-country">{t("dispute.country_label")}</label>
                        <select id="d-country" className="input-line mt-2 bg-ink-deep" value={country} onChange={(e) => setCountry(e.target.value)} data-testid="dispute-country">
                            <option value="">—</option>
                            <optgroup label="— Preferred —">
                                {options.preferred.map((c) => <option key={`p-${c.code}`} value={c.code}>{c.name}</option>)}
                            </optgroup>
                            <optgroup label="— All —">
                                {options.rest.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                            </optgroup>
                        </select>
                    </div>

                    <div>
                        <label className="label-eyebrow" htmlFor="d-pres">{t("dispute.presence_label")}</label>
                        <input id="d-pres" className="input-line mt-2" placeholder={t("dispute.presence_ph")} value={presenceRef} onChange={(e) => setPresenceRef(e.target.value)} data-testid="dispute-presence" />
                    </div>

                    <div>
                        <label className="label-eyebrow" htmlFor="d-desc">{t("dispute.description_label")}</label>
                        <textarea id="d-desc" rows={7} className="textarea-paper mt-2" placeholder={t("dispute.description_ph")} value={description} onChange={(e) => setDescription(e.target.value)} required data-testid="dispute-description" />
                    </div>

                    {err && <p className="text-ink-danger font-serif italic" data-testid="dispute-error">{err}</p>}

                    <div className="pt-6 border-t border-ink-border">
                        <button
                            type="submit"
                            disabled={!canSubmit || busy}
                            className="btn-primary inline-flex items-center gap-2"
                            data-testid="dispute-submit"
                        >
                            <Send size={16} /> {busy ? t("dispute.submitting") : t("dispute.submit")}
                        </button>
                    </div>
                </form>
            </main>
        </div>
    );
}
